import { css } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import { Fragment } from 'react';
import { IconButton, Typography } from '@mui/material';
import { colors } from '../../../../theme/colors';
import { IStoryChip } from './security-story-model';

/**
 * The banner sits above the table, chips joined by arrows, with a dismiss button at the end.
 *
 * A whole restructure chain can run to five or six chips, so they wrap onto a second row rather than
 * being dropped: every event in a chain has to be reachable, and a chain running off the edge is
 * indistinguishable from one that truncates.
 */
const banner = css`
  align-items: stretch;
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  padding: 0 0.5rem 0.75rem;
`;

const chipCard = css`
  background: var(--mui-palette-background-paper);
  border: 1.5px solid ${colors.secondary[40]};
  border-radius: 0.6875rem;
  /* Narrow enough that a six-chip chain fits two rows rather than six. */
  flex: 1 1 9rem;
  min-width: 0;
  padding: 0.375rem 0.625rem;
`;

const chipTitle = css`
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const chipValue = css`
  font-variant-numeric: tabular-nums;
  font-weight: 700;
`;

const chipDetail = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const arrow = css`
  align-self: center;
  color: ${colors.secondary[40]};
  flex-shrink: 0;
`;

const toneColor: Record<IStoryChip['tone'], string> = {
  neutral: colors.secondary[50],
  gain: colors.success[70],
  loss: colors.critical[50],
};

interface IStoryBannerProps {
  chips: IStoryChip[];
  /** Names the security the story belongs to, for the dismiss button's label. */
  securityName: string;
  onDismiss: () => void;
}

export function StoryBanner({ chips, securityName, onDismiss }: IStoryBannerProps) {
  if (chips.length === 0) {
    return null;
  }

  return (
    <div css={banner} role='group' aria-label={`Lifecycle of ${securityName}`}>
      {chips.map((chip, index) => (
        <Fragment key={chip.id}>
          {index > 0 && (
            <span css={arrow} aria-hidden>
              {'\u2192'}
            </span>
          )}
          <div css={chipCard}>
            <Typography css={chipTitle} style={{ color: toneColor[chip.tone] }}>
              {chip.title}
            </Typography>
            <Typography variant='body2' css={chipValue}>
              {chip.value}
            </Typography>
            {chip.detail !== '' && (
              <Typography variant='caption' color='text.secondary' css={chipDetail} component='p'>
                {chip.detail}
              </Typography>
            )}
          </div>
        </Fragment>
      ))}
      <IconButton
        aria-label={`Dismiss lifecycle of ${securityName}`}
        css={arrow}
        onClick={onDismiss}
        size='small'
      >
        <CloseIcon fontSize='small' />
      </IconButton>
    </div>
  );
}
